<div class="dynamic-form-container" role="main">
  @if (formConfig) {
    <!-- Form Header -->
    @if (formConfig.banner) {
      <div class="form-banner">
        <h2 class="pt-3" id="form-title-{{ formConfig.id }}">{{ formConfig.banner }}</h2>
        
        <!-- Action Buttons in Header -->
        @if (!readonly && !hideFormActions) {
          <div class="form-header-actions" role="group" aria-label="Form actions">
            @if (!isFormCompleted) {
              <ppl-button
                label="Save Form"
                [customClass]="'primary'"
                [size]="'medium'"
                [disabled]="!canSave || saveInProgress"
                [loading]="saveInProgress"
                [attr.aria-label]="saveInProgress ? 'Saving form, please wait' : 'Save form'"
                [attr.aria-disabled]="!canSave || saveInProgress"
                (handleClick)="onSave()"
              ></ppl-button>
            } @else {
              <div class="completed-status-header" role="status" aria-live="polite">
                <span class="completed-badge-header" aria-label="Form completed">✓ Completed</span>
                <ppl-button
                  label="Edit"
                  [customClass]="'secondary'"
                  [size]="'medium'"
                  aria-label="Edit form"
                  (handleClick)="onEdit()"
                ></ppl-button>
              </div>
            }
          </div>
        }
      </div>
    }

    @if (formConfig.description) {
      <div class="form-description" id="form-description-{{ formConfig.id }}">
        <p [innerHTML]="formConfig.description"></p>
      </div>
    }

    <!-- Error Message -->
    <div aria-live="polite" aria-atomic="true" class="sr-announcements">
      @if (saveError) {
        <nz-alert 
          nzType="error" 
          [nzMessage]="errorMessage" 
          nzShowIcon 
          [nzCloseable]="true"
          role="alert"
          aria-label="Error message"
        ></nz-alert>
      }
    </div>

    <!-- Form Sections -->
    <form 
      [formGroup]="formGroup" 
      class="dynamic-form"
      [attr.aria-labelledby]="formConfig.banner ? 'form-title-' + formConfig.id : null"
      [attr.aria-describedby]="formConfig.description ? 'form-description-' + formConfig.id : null"
      novalidate
    >
      @for (section of formConfig.sections; track section.id; let sectionIndex = $index) {
        @if (isSectionVisible(section)) {
          <div class="form-section" role="group" [attr.aria-labelledby]="'section-title-' + section.id">
            <h3 class="section-title" [id]="'section-title-' + section.id">{{ section.title }}</h3>
            @if (section.description) {
              <p class="section-description" [id]="'section-description-' + section.id" [innerHTML]="section.description"></p>
            }

            <div class="form-fields">
              @for (field of section.fields; track field.id) {
                @if (isFieldVisible(field)) {
                  <div class="form-field" [ngClass]="field.cssClass" [class.field-gated-off]="isFieldGatedOff(field)">
                    
                    <!-- Text Input -->
                    @if (field.type === 'text' || field.type === 'email' || field.type === 'tel') {
                      <label [for]="field.id">
                        {{ getFieldLabel(field) }}
                        @if (field.required) {
                          <span class="required" aria-label="required">*</span>
                        }
                      </label>
                      @if (field.disabled) {
                        <input
                          nz-input
                          [id]="field.id"
                          [value]="getFormattedValue(field)"
                          [disabled]="true"
                          [attr.aria-label]="field.label + ' (read-only)'"
                          [attr.aria-readonly]="true"
                          class="calculated-field"
                        />
                      } @else {
                        <input 
                          nz-input 
                          [formControlName]="field.id"
                          [id]="field.id"
                          [type]="field.type"
                          [placeholder]="field.placeholder || ''"
                          [maxlength]="field.maxLength"
                          [attr.aria-required]="field.required || null"
                          [attr.aria-invalid]="getErrorMessage(field) ? true : null"
                          [attr.aria-describedby]="getAriaDescribedBy(field)"
                        />
                      }
                      @if (field.description) {
                        <small class="field-description" [id]="'desc-' + field.id">{{ field.description }}</small>
                      }
                      @if (getErrorMessage(field)) {
                        <div class="error-message" [id]="'error-' + field.id" role="alert" aria-live="polite">{{ getErrorMessage(field) }}</div>
                      }
                    }

                    <!-- Number Input -->
                    @if (field.type === 'number') {
                      <label [for]="field.id">
                        {{ getFieldLabel(field) }}
                        @if (field.required) {
                          <span class="required" aria-label="required">*</span>
                        }
                      </label>
                      <input 
                        nz-input 
                        [formControlName]="field.id"
                        [id]="field.id"
                        type="number"
                        [attr.step]="field.integerOnly ? '1' : null"
                        (keydown)="field.integerOnly ? preventDecimalInput($event) : null"
                        [placeholder]="field.placeholder || ''"
                        [attr.aria-required]="field.required || null"
                        [attr.aria-invalid]="getErrorMessage(field) ? true : null"
                        [attr.aria-describedby]="getAriaDescribedBy(field)"
                      />
                      @if (field.description) {
                        <small class="field-description" [id]="'desc-' + field.id">{{ field.description }}</small>
                      }
                      @if (getErrorMessage(field)) {
                        <div class="error-message" [id]="'error-' + field.id" role="alert" aria-live="polite">{{ getErrorMessage(field) }}</div>
                      }
                    }

                    <!-- Textarea -->
                    @if (field.type === 'textarea') {
                      <label [for]="field.id">
                        {{ getFieldLabel(field) }}
                        @if (field.required) {
                          <span class="required" aria-label="required">*</span>
                        }
                      </label>
                      <textarea 
                        nz-input 
                        [formControlName]="field.id"
                        [id]="field.id"
                        [placeholder]="field.placeholder || ''"
                        [rows]="field.rows || 3"
                        [maxlength]="field.maxLength"
                        [attr.aria-required]="field.required || null"
                        [attr.aria-invalid]="getErrorMessage(field) ? true : null"
                        [attr.aria-describedby]="getAriaDescribedBy(field)"
                      ></textarea>
                      @if (field.description) {
                        <small class="field-description" [id]="'desc-' + field.id">{{ field.description }}</small>
                      }
                      @if (getErrorMessage(field)) {
                        <div class="error-message" [id]="'error-' + field.id" role="alert" aria-live="polite">{{ getErrorMessage(field) }}</div>
                      }
                    }

                    <!-- Radio Buttons -->
                    @if (field.type === 'radio' && field.options) {
                      <fieldset role="group" [attr.aria-describedby]="getAriaDescribedBy(field)">
                        <legend>
                          {{ getFieldLabel(field) }}
                          @if (field.required) {
                            <span class="required" aria-label="required">*</span>
                          }
                        </legend>
                        @if (field.description) {
                          <small class="field-description" [id]="'desc-' + field.id">{{ field.description }}</small>
                        }
                        <nz-radio-group
                          [formControlName]="field.id"
                          [attr.aria-required]="field.required || null"
                          [attr.aria-invalid]="getErrorMessage(field) ? true : null"
                        >
                          @for (option of field.options; track option.value) {
                            <label nz-radio [nzValue]="option.value" [nzDisabled]="!!option.disabled" [attr.aria-label]="option.label">
                              {{ option.label }}
                              @if (option.description) {
                                <small class="option-description">{{ option.description }}</small>
                              }
                            </label>
                          }
                        </nz-radio-group>
                        @if (getErrorMessage(field)) {
                          <div class="error-message" [id]="'error-' + field.id" role="alert" aria-live="polite">{{ getErrorMessage(field) }}</div>
                        }
                      </fieldset>
                    }

                    <!-- Checkboxes -->
                    @if (field.type === 'checkbox' && field.options) {
                      <fieldset
                        role="group"
                        [attr.aria-describedby]="getAriaDescribedBy(field)"
                        [attr.aria-label]="showCheckboxLegend(field) ? null : field.label"
                      >
                        @if (showCheckboxLegend(field)) {
                          <legend>
                          {{ getFieldLabel(field) }}
                            @if (field.required) {
                              <span class="required" aria-label="required">*</span>
                            }
                          </legend>
                        }
                        @if (field.description) {
                          <small class="field-description" [id]="'desc-' + field.id">{{ field.description }}</small>
                        }
                        @if (field.options.length > 1) {
                          <div
                            class="checkbox-group"
                            role="group"
                            [attr.aria-required]="field.required || null"
                            [attr.aria-invalid]="getErrorMessage(field) ? true : null"
                          >
                            @for (option of field.options; track option.value) {
                              <label
                                nz-checkbox
                                [nzChecked]="isMultiCheckboxSelected(field.id, option.value)"
                                [nzDisabled]="!!option.disabled || !!formGroup.get(field.id)?.disabled"
                                (nzCheckedChange)="onMultiCheckboxChange(field.id, option.value, $event)"
                                [attr.aria-label]="option.label"
                              >
                                {{ option.label }}
                                @if (option.description) {
                                  <small class="option-description">{{ option.description }}</small>
                                }
                              </label>
                            }
                          </div>
                        } @else {
                          <div class="checkbox-group" role="group">
                            <label
                              nz-checkbox
                              [nzChecked]="isSingleCheckboxChecked(field.id, field.options[0].value)"
                              [nzDisabled]="!!formGroup.get(field.id)?.disabled"
                              (nzCheckedChange)="onSingleCheckboxChange(field.id, field.options[0].value, $event)"
                              [attr.aria-label]="field.options[0].label"
                            >
                              {{ field.options[0].label }}
                              @if (field.options[0].description) {
                                <small class="option-description">{{ field.options[0].description }}</small>
                              }
                            </label>
                          </div>
                        }
                        @if (getErrorMessage(field)) {
                          <div class="error-message" [id]="'error-' + field.id" role="alert" aria-live="polite">{{ getErrorMessage(field) }}</div>
                        }
                        @if (field.note) {
                          <small class="field-note" [id]="'note-' + field.id">{{ field.note }}</small>
                        }
                      </fieldset>
                    }

                    <!-- Dropdown/Select -->
                    @if (field.type === 'dropdown' && (field.options || field.optionsSource)) {
                      <label [for]="field.id">
                        {{ getFieldLabel(field) }}
                        @if (field.required) {
                          <span class="required" aria-label="required">*</span>
                        }
                      </label>
                      @if (field.description) {
                        <small class="field-description" [id]="'desc-' + field.id">{{ field.description }}</small>
                      }
                      <nz-select 
                        [formControlName]="field.id"
                        [id]="field.id"
                        [nzPlaceHolder]="field.placeholder || 'Select'"
                        [attr.aria-required]="field.required || null"
                        [attr.aria-invalid]="getErrorMessage(field) ? true : null"
                        [attr.aria-describedby]="getAriaDescribedBy(field)"
                      >
                        @for (option of getFieldOptions(field); track option.value) {
                          <nz-option [nzValue]="option.value" [nzLabel]="option.label"></nz-option>
                        }
                      </nz-select>
                      @if (getErrorMessage(field)) {
                        <div class="error-message" [id]="'error-' + field.id" role="alert" aria-live="polite">{{ getErrorMessage(field) }}</div>
                      }
                    }

                    <!-- Date Picker -->
                    @if (field.type === 'date') {
                      <label [for]="field.id">
                        {{ getFieldLabel(field) }}
                        @if (field.required) {
                          <span class="required" aria-label="required">*</span>
                        }
                      </label>
                      @if (field.description) {
                        <small class="field-description" [id]="'desc-' + field.id">{{ field.description }}</small>
                      }
                      <nz-date-picker
                        [formControlName]="field.id"
                        [id]="field.id"
                        nzFormat="MM-dd-yyyy"
                        [nzPlaceHolder]="field.placeholder || 'Select date'"
                        style="width: 100%;"
                        [attr.aria-required]="field.required || null"
                        [attr.aria-invalid]="getErrorMessage(field) ? true : null"
                        [attr.aria-describedby]="getAriaDescribedBy(field)"
                      ></nz-date-picker>
                      @if (getErrorMessage(field)) {
                        <div class="error-message" [id]="'error-' + field.id" role="alert" aria-live="polite">{{ getErrorMessage(field) }}</div>
                      }
                    }

                    <!-- Table (repeating rows) -->
                    @if (field.type === 'table' && field.columns) {
                      <fieldset class="table-field">
                        <legend>
                          {{ field.label }}
                          @if (field.required) {
                            <span class="required" aria-label="required">*</span>
                          }
                        </legend>
                        @if (field.description) {
                          <small class="field-description" [id]="'desc-' + field.id">{{ field.description }}</small>
                        }

                        <div class="dynamic-table-wrapper">
                          <table class="dynamic-table">
                            <caption class="sr-only">{{ field.label }}</caption>
                            <thead>
                              <tr>
                                <th scope="col" class="row-number-col">#</th>
                                @for (column of getVisibleColumns(field); track column.id) {
                                  <th scope="col" [style.width]="column.width">
                                    {{ column.label }}
                                    @if (column.required) {
                                      <span class="required" aria-label="required">*</span>
                                    }
                                  </th>
                                }
                                @if (showRowActions(field)) {
                                  <th scope="col" class="row-actions-col">Actions</th>
                                }
                              </tr>
                            </thead>
                            <tbody>
                              @for (row of getVisibleRows(field); track row) {
                                <tr>
                                  <th scope="row" class="row-number-col">{{ row }}</th>
                                  @for (cell of getRowCells(field, row); track cell.id) {
                                    <td>
                                      <!-- A cell nobody can edit reads as text: a disabled input
                                           is just a greyed box the user still tries to click. -->
                                      @if (isCellReadonly(cell)) {
                                        <span class="cell-readonly" [attr.aria-label]="cell.label">{{ getCellText(cell) }}</span>
                                      }
                                      @if (!isCellReadonly(cell) && (cell.type === 'text' || cell.type === 'email' || cell.type === 'tel')) {
                                        <input
                                          nz-input
                                          [formControlName]="cell.id"
                                          [id]="cell.id"
                                          [type]="cell.type"
                                          [placeholder]="cell.placeholder || ''"
                                          [maxlength]="cell.maxLength"
                                          [attr.aria-label]="cell.label"
                                          [attr.aria-required]="cell.required || null"
                                          [attr.aria-invalid]="getErrorMessage(cell) ? true : null"
                                          [attr.aria-describedby]="getErrorMessage(cell) ? 'error-' + cell.id : null"
                                        />
                                      }
                                      @if (!isCellReadonly(cell) && cell.type === 'number') {
                                        <input
                                          nz-input
                                          [formControlName]="cell.id"
                                          [id]="cell.id"
                                          type="number"
                                          [attr.step]="cell.integerOnly ? '1' : null"
                                          (keydown)="cell.integerOnly ? preventDecimalInput($event) : null"
                                          [placeholder]="cell.placeholder || ''"
                                          [attr.aria-label]="cell.label"
                                          [attr.aria-required]="cell.required || null"
                                          [attr.aria-invalid]="getErrorMessage(cell) ? true : null"
                                          [attr.aria-describedby]="getErrorMessage(cell) ? 'error-' + cell.id : null"
                                        />
                                      }
                                      @if (!isCellReadonly(cell) && cell.type === 'textarea') {
                                        <textarea
                                          nz-input
                                          [formControlName]="cell.id"
                                          [id]="cell.id"
                                          [placeholder]="cell.placeholder || ''"
                                          [rows]="cell.rows || 2"
                                          [maxlength]="cell.maxLength"
                                          [attr.aria-label]="cell.label"
                                          [attr.aria-required]="cell.required || null"
                                          [attr.aria-invalid]="getErrorMessage(cell) ? true : null"
                                          [attr.aria-describedby]="getErrorMessage(cell) ? 'error-' + cell.id : null"
                                        ></textarea>
                                      }
                                      @if (!isCellReadonly(cell) && cell.type === 'dropdown' && (cell.options || cell.optionsSource)) {
                                        <nz-select
                                          [formControlName]="cell.id"
                                          [id]="cell.id"
                                          [nzPlaceHolder]="cell.placeholder || 'Select'"
                                          nzShowSearch
                                          nzAllowClear
                                          [nzNotFoundContent]="cell.optionsSource ? 'No authorized services available for the agreement term.' : 'No options available.'"
                                          [attr.aria-label]="cell.label"
                                          [attr.aria-required]="cell.required || null"
                                          [attr.aria-invalid]="getErrorMessage(cell) ? true : null"
                                        >
                                          @for (option of getFieldOptions(cell); track option.value) {
                                            <nz-option [nzValue]="option.value" [nzLabel]="option.label"></nz-option>
                                          }
                                        </nz-select>
                                      }
                                      @if (!isCellReadonly(cell) && cell.type === 'date') {
                                        <nz-date-picker
                                          [formControlName]="cell.id"
                                          [id]="cell.id"
                                          nzFormat="MM-dd-yyyy"
                                          [nzPlaceHolder]="cell.placeholder || 'Select date'"
                                          style="width: 100%;"
                                          [attr.aria-label]="cell.label"
                                          [attr.aria-required]="cell.required || null"
                                        ></nz-date-picker>
                                      }
                                      @if (getErrorMessage(cell)) {
                                        <div class="error-message" [id]="'error-' + cell.id" role="alert" aria-live="polite">{{ getErrorMessage(cell) }}</div>
                                      }
                                    </td>
                                  }
                                  @if (showRowActions(field)) {
                                    <td class="row-actions-col">
                                      <div class="row-actions">
                                        @if (field.allowDuplicateRow !== false) {
                                          <button
                                            type="button"
                                            class="row-action-symbol"
                                            [disabled]="!canDuplicateRow(field)"
                                            [attr.aria-label]="'Duplicate row ' + row"
                                            [title]="'Duplicate row ' + row"
                                            (click)="duplicateTableRow(field, row)"
                                          >
                                            <span nz-icon nzType="copy" nzTheme="outline" aria-hidden="true"></span>
                                          </button>
                                        }
                                        @if (field.allowRemoveRow !== false) {
                                          <button
                                            type="button"
                                            class="row-action-symbol row-action-remove"
                                            [disabled]="!canRemoveRow(field)"
                                            [attr.aria-label]="'Remove row ' + row"
                                            [title]="'Remove row ' + row"
                                            (click)="removeTableRow(field, row)"
                                          >
                                            <span nz-icon nzType="delete" nzTheme="outline" aria-hidden="true"></span>
                                          </button>
                                        }
                                      </div>
                                    </td>
                                  }
                                </tr>
                              }
                            </tbody>
                          </table>
                        </div>

                        <div class="table-actions">
                          @if (field.allowAddRow !== false && !readonly) {
                            <button
                              type="button"
                              class="table-add-row"
                              [disabled]="!canAddRow(field)"
                              [attr.aria-label]="'Add a row to ' + field.label"
                              (click)="addTableRow(field)"
                            >
                              <span nz-icon nzType="plus" nzTheme="outline" aria-hidden="true"></span>
                              {{ field.addRowLabel || 'Add Row' }}
                            </button>
                          }
                          <small class="table-row-count" role="status" aria-live="polite">
                            @if (hasRowLimit(field)) {
                              {{ getRowCount(field) }} of {{ getMaxRows(field) }} rows used
                            } @else {
                              {{ getRowCount(field) }} {{ getRowCount(field) === 1 ? 'row' : 'rows' }}
                            }
                          </small>
                        </div>
                      </fieldset>
                    }

                  </div>
                }
              }
            </div>
          </div>
        }
      }
    </form>
  }
</div>
